import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Backdrop,
  Box,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Divider,
  Container,
  Paper,
  useTheme,
  alpha,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
} from '@mui/icons-material';
import api from '../services/api';
import { Helmet } from 'react-helmet-async';
import LegalBusinessDetails from './LegalBusinessDetails';

const Login = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [unverifiedAccount, setUnverifiedAccount] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });
  const navigate = useNavigate();
  const theme = useTheme();

  const toggleDarkMode = () => {
    setDarkMode((prev) => {
      const newMode = !prev;
      localStorage.setItem('darkMode', JSON.stringify(newMode));
      window.location.reload();
      return newMode;
    });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (loading) {
      return;
    }

    setLoading(true);
    setError('');
    setInfoMessage('');
    setUnverifiedAccount(false);

    try {
      const response = await api.post('/auth/login', formData);
      const { token, refresh_token: refreshToken } = response.data;

      api.setSessionTokens(token, refreshToken ?? null);

      // Fetch complete user data to ensure all fields (including tenant_name) are populated
      const userResponse = await api.authApi.getCurrentUser();
      const user = userResponse.data;

      localStorage.setItem('currentUser', JSON.stringify(user));
      
      // Call onLogin callback with complete user data
      onLogin(user);

      // Navigate to dashboard
      navigate('/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      const statusCode = error.response?.status;
      const errorMessage = error.response?.data?.error || 'Invalid credentials';
      setUnverifiedAccount(statusCode === 403);
      setError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    const email = formData.email.trim().toLowerCase();
    if (!email) {
      setError('Enter your email address before requesting another verification email.');
      return;
    }

    setResendLoading(true);
    setError('');
    setInfoMessage('');

    try {
      const response = await api.authApi.resendVerification(email);
      setInfoMessage(response.data?.message || 'Verification email sent. Check your inbox.');
      setUnverifiedAccount(true);
    } catch (resendError) {
      setError(resendError.response?.data?.error || 'Could not resend the verification email. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <>
      <Helmet>
        <title>Log In — Struxor Construction Software</title>
        <meta name="description" content="Log in to your Struxor account to manage construction projects, quotes, timesheets, RAMS, and on-site team collaboration." />
        <link rel="canonical" href="https://www.struxorapp.com/login" />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <Backdrop
        open={loading}
        sx={{
          zIndex: (muiTheme) => muiTheme.zIndex.modal + 1,
          color: '#fff',
          backgroundColor: alpha(theme.palette.common.black, 0.7),
          backdropFilter: 'blur(4px)',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="inherit" size={36} thickness={4.5} />
          <Typography variant="body1" sx={{ fontWeight: 600, letterSpacing: 0.2 }}>
            Signing you in...
          </Typography>
        </Box>
      </Backdrop>
      <Box
      sx={{
        minHeight: '100vh',
        backgroundImage: 'url(/background_image.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: theme.palette.mode === 'light' 
            ? 'linear-gradient(180deg, rgba(17,24,39,0.45) 0%, rgba(17,24,39,0.6) 60%, rgba(17,24,39,0.75) 100%)'
            : 'linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.8) 60%, rgba(0,0,0,0.9) 100%)',
          zIndex: 1,
        },
      }}
    >
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 2 }}>
        {/* Dark Mode Toggle */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={darkMode}
                onChange={toggleDarkMode}
                icon={<LightModeIcon sx={{ color: 'rgba(255,255,255,0.8)' }} />}
                checkedIcon={<DarkModeIcon sx={{ color: 'rgba(255,255,255,0.8)' }} />}
              />
            }
            label=""
          />
        </Box>
        
        <Paper
          elevation={24}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            bgcolor: 'background.paper',
          }}
        >
          {/* Header Section */}
          <Box
            sx={{
              background: theme.palette.mode === 'light' 
                ? 'linear-gradient(45deg, #f8f9fa, #e9ecef)' 
                : `linear-gradient(45deg, ${alpha(theme.palette.background.paper, 0.8)}, ${alpha(theme.palette.background.default, 0.8)})`,
              color: 'text.primary',
              textAlign: 'center',
              py: 4,
              px: 3,
            }}
          >
            <Box
              component="img"
              src="/struxor.png"
              alt="Struxor logo"
              sx={{ 
                height: 56, 
                mb: 1.5, 
                filter: theme.palette.mode === 'dark' 
                  ? 'brightness(0) invert(1) drop-shadow(0 2px 2px rgba(0,0,0,0.3))' 
                  : 'drop-shadow(0 2px 2px rgba(0,0,0,0.1))' 
              }}
            />
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Sign in to manage your projects
            </Typography>
          </Box>

          <CardContent sx={{ p: 4 }}>
            <form onSubmit={handleSubmit} noValidate>
              {error && (
                <Alert 
                  severity="error" 
                  sx={{ 
                    mb: 3,
                    borderRadius: 2,
                    '& .MuiAlert-icon': {
                      fontSize: '1.2rem'
                    }
                  }}
                >
                  {error}
                </Alert>
              )}

              {infoMessage && (
                <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                  {infoMessage}
                </Alert>
              )}

              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={loading}
                  required
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                />
              </Box>

              <Box sx={{ mb: 4 }}>
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  disabled={loading}
                  required
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleTogglePasswordVisibility}
                          edge="end"
                          size="small"
                          disabled={loading}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                    },
                  }}
                />
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: `0 4px 15px ${alpha(theme.palette.primary.main, 0.4)}`,
                  '&:hover': {
                    boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.6)}`,
                    transform: 'translateY(-1px)',
                  },
                  '&:disabled': {
                    background: theme.palette.grey[300],
                    boxShadow: 'none',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>

              {unverifiedAccount && (
                <Button
                  fullWidth
                  variant="text"
                  onClick={handleResendVerification}
                  disabled={loading || resendLoading}
                  sx={{ mt: 2, borderRadius: 2, fontWeight: 600 }}
                >
                  {resendLoading ? 'Sending verification email...' : 'Resend Verification Email'}
                </Button>
              )}

              <Divider sx={{ my: 3 }}>
                <Typography variant="body2" color="textSecondary">
                  or
                </Typography>
              </Divider>

              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Don't have an account?
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/register')}
                  disabled={loading}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 3,
                    mr: 1,
                    '&:hover': {
                      background: alpha(theme.palette.primary.main, 0.1),
                    },
                  }}
                >
                  Register
                </Button>
                <Button
                  variant="text"
                  onClick={() => navigate('/')}
                  disabled={loading}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 3,
                    '&:hover': {
                      background: alpha(theme.palette.primary.main, 0.1),
                    },
                  }}
                >
                  Home
                </Button>
              </Box>
            </form>
          </CardContent>
        </Paper>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <LegalBusinessDetails
            compact
            textAlign="center"
            textColor="rgba(255, 255, 255, 0.82)"
            linkColor="rgba(255, 255, 255, 0.95)"
            showLegalLinks
          />
        </Box>
      </Container>
    </Box>
    </>
  );
};

export default Login;
